import React, { useEffect, useState } from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { HeroSection, HeroContent, GradientButton, SectionContainer, FeatureCard } from "../Components/StyledComponents";
import { loadGallery } from "../api/gallery";
import ChairIcon from '@mui/icons-material/Chair';
import BuildIcon from '@mui/icons-material/Build';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import Seo, { SITE_URL } from "../Components/Seo";
import {
  BUSINESS_EMAIL,
  BUSINESS_NAME,
  BUSINESS_PHONE,
  FACEBOOK_URL,
  GOOGLE_REVIEWS_URL,
  INSTAGRAM_URL,
} from "../config/business";

const Home = () => {
  const [heroImage, setHeroImage] = useState("");

  useEffect(() => {
    loadGallery()
      .then((gallery) => {
        const featuredProject = gallery.projects.find((project) => project.cover?.src);
        setHeroImage(featuredProject?.cover?.src || gallery.servicesGallery[0]?.src || "");
      })
      .catch(() => {
        setHeroImage("");
      });
  }, []);

  const features = [
    {
      icon: <ChairIcon sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />,
      title: "Vakmanschap",
      description: "Jarenlange ervaring in hoogwaardige meubelstoffering"
    },
    {
      icon: <BuildIcon sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />,
      title: "Maatwerk",
      description: "Elke opdracht is uniek en wordt met zorg uitgevoerd"
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />,
      title: "Kwaliteit",
      description: "Premium materialen en perfecte afwerking"
    },
    {
      icon: <EmojiObjectsIcon sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />,
      title: "Advies",
      description: "Persoonlijk advies voor uw stoffeerproject"
    },
  ];

  return (
    <Box>
      <Seo
        title="Meester Stoffeerders | Meubelstoffering en Restauratie in Almere"
        description="Professionele meubelstoffering, herstoffering en restauratie in Almere. Voor banken, stoelen, fauteuils en maatwerk stoffeerprojecten."
        path="/"
        schema={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: BUSINESS_NAME,
          url: SITE_URL,
          telephone: BUSINESS_PHONE,
          email: BUSINESS_EMAIL,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Argonweg 66",
            addressLocality: "Almere",
            addressCountry: "NL",
          },
          openingHours: ["Mo-Fr 08:00-17:00"],
          areaServed: "Almere en omgeving",
          image: heroImage || `${SITE_URL}/logo512.png`,
          sameAs: [INSTAGRAM_URL, FACEBOOK_URL, GOOGLE_REVIEWS_URL],
        }}
      />
      {/* Hero Section */}
      <HeroSection heroImage={heroImage}>
        <HeroContent maxWidth="md">
          <Typography 
            variant="h1" 
            sx={{ 
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
            }}
          >
            Meester Stoffeerders
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 4, 
              opacity: 0.95,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              fontWeight: 300,
            }}
          >
            Uw specialist in professionele meubelstoffering en restauratie
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
            <GradientButton 
              component={Link} 
              to="/projecten"
              size="large"
            >
              Bekijk Onze Projecten
            </GradientButton>
            <GradientButton 
              component={Link} 
              to="/contact"
              size="large"
              sx={{ 
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              Contact Opnemen
            </GradientButton>
          </Box>
        </HeroContent>
      </HeroSection>

      {/* Features Section */}
      <SectionContainer sx={{ backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ mb: 6, color: 'primary.main', fontWeight: 700 }}
          >
            Waarom Kiezen voor Ons?
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <FeatureCard>
                  {feature.icon}
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </SectionContainer>

      {/* Call to Action Section */}
      <SectionContainer sx={{ backgroundColor: 'primary.main', color: 'white', textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
            Klaar om te Beginnen?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Neem vandaag nog contact met ons op voor een vrijblijvend adviesgesprek
          </Typography>
          <GradientButton 
            component={Link} 
            to="/contact"
            size="large"
          >
            Neem Contact Op
          </GradientButton>
        </Container>
      </SectionContainer>
    </Box>
  );
};

export default Home;
